import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import {
  BusinessProfileResponse,
  UpdateBusinessProfileRequest,
  UpdateBusinessProfileResponse,
  UpdateLogoResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChangeEmailRequest,
  ChangeEmailResponse,
  BusinessSiteInfo,
  UpdateBusinessSiteRequest,
  BusinessPrivacyInfo
} from '../models/sala.model';
import { environment } from '../../environments/environment';

export type RefundFeeType = 'none' | 'percent' | 'fixed';

export interface CancellationPolicyInput {
  cancellation_min_hours: number;
  refund_fee_type: RefundFeeType;
  refund_fee_value: number;
}

export interface CancellationPolicyResponse {
  policy: CancellationPolicyInput & { configured: boolean };
  configured_at: string | null;
  options: { hours: number[]; default_hours: number; max_percent: number; max_fixed: number };
}
import { friendlyError } from '../utils/friendly-error';

@Injectable({
  providedIn: 'root'
})
export class BusinessConfigService {
  private readonly API_URL = `${environment.apiUrl}/business`;

  constructor(private http: HttpClient) {}

  /**
   * Obtener perfil del negocio
   */
  obtenerPerfil(businessId: number): Observable<BusinessProfileResponse> {
    return this.http.get<BusinessProfileResponse>(`${this.API_URL}/${businessId}/profile`).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar información del perfil
   */
  actualizarPerfil(businessId: number, profile: UpdateBusinessProfileRequest): Observable<UpdateBusinessProfileResponse> {
    return this.http.put<UpdateBusinessProfileResponse>(`${this.API_URL}/${businessId}/profile`, profile).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Actualizar logo del negocio
   */
  actualizarLogo(businessId: number, logoFile: File): Observable<UpdateLogoResponse> {
    const formData = new FormData();
    formData.append('archivo', logoFile, logoFile.name);

    return this.http.patch<UpdateLogoResponse>(`${this.API_URL}/${businessId}/logo`, formData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cambiar contraseña del negocio
   */
  cambiarContrasena(businessId: number, passwordData: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.http.patch<ChangePasswordResponse>(`${this.API_URL}/${businessId}/password`, passwordData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Cambia el correo de ACCESO (login) del propietario autenticado -- no el
   * correo de contacto público del negocio (ese sigue editándose aparte,
   * bloqueado desde el perfil por su vínculo con el login legado).
   */
  cambiarCorreo(businessId: number, emailData: ChangeEmailRequest): Observable<ChangeEmailResponse> {
    return this.http.patch<ChangeEmailResponse>(`${this.API_URL}/${businessId}/email`, emailData).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * "Mi sitio": enlace público personalizado del negocio (tusala.net/:slug).
   * El GET autogenera y persiste un slug la primera vez que no exista.
   */
  obtenerSitio(businessId: number): Observable<BusinessSiteInfo> {
    return this.http.get<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site`).pipe(
      catchError(this.handleError)
    );
  }

  actualizarSlugSitio(businessId: number, slug: string): Observable<BusinessSiteInfo> {
    return this.http.patch<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site`, { slug }).pipe(
      catchError(this.handleError)
    );
  }

  /** Guarda texto del hero y/o colores del sitio (cualquier combinación de campos). */
  actualizarPersonalizacionSitio(businessId: number, data: UpdateBusinessSiteRequest): Observable<BusinessSiteInfo> {
    return this.http.patch<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site`, data).pipe(
      catchError(this.handleError)
    );
  }

  actualizarBannerSitio(businessId: number, bannerFile: File): Observable<BusinessSiteInfo> {
    const formData = new FormData();
    formData.append('archivo', bannerFile, bannerFile.name);
    return this.http.patch<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site/banner`, formData).pipe(
      catchError(this.handleError)
    );
  }

  /** Imagen para la tarjeta de vista previa al compartir el enlace -- el
   * backend siempre la recorta a 1200x630, sin importar el tamaño subido. */
  actualizarImagenCompartirSitio(businessId: number, imageFile: File): Observable<BusinessSiteInfo> {
    const formData = new FormData();
    formData.append('archivo', imageFile, imageFile.name);
    return this.http.patch<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site/share-image`, formData).pipe(
      catchError(this.handleError)
    );
  }

  /** Publica el borrador actual: a partir de este momento el sitio público
   * real refleja exactamente lo que hoy es el borrador. */
  publicarSitio(businessId: number): Observable<BusinessSiteInfo> {
    return this.http.post<BusinessSiteInfo>(`${this.API_URL}/${businessId}/site/publish`, {}).pipe(
      catchError(this.handleError)
    );
  }

  /** Negocio privado (código de acceso de 4 dígitos) -- ver
   * Business.is_private/access_code. Compartido entre "Mi sitio", "Gestión
   * de salas" y el ícono de la barra superior: se actualiza solo cada vez
   * que cualquiera de los dos métodos de abajo se llama, sin que cada
   * componente tenga que sincronizarlo a mano. */
  readonly privacyState = signal<BusinessPrivacyInfo | null>(null);

  obtenerPrivacidad(businessId: number): Observable<BusinessPrivacyInfo> {
    return this.http.get<BusinessPrivacyInfo>(`${this.API_URL}/${businessId}/privacy`).pipe(
      tap(info => this.privacyState.set(info)),
      catchError(this.handleError)
    );
  }

  actualizarPrivacidad(businessId: number, data: { is_private?: boolean; regenerate_code?: boolean }): Observable<BusinessPrivacyInfo> {
    return this.http.patch<BusinessPrivacyInfo>(`${this.API_URL}/${businessId}/privacy`, data).pipe(
      tap(info => this.privacyState.set(info)),
      catchError(this.handleError)
    );
  }

  /** Política de cancelación propia del negocio (anticipación mínima y
   * cargo administrativo opcional sobre reembolsos). */
  obtenerPoliticaCancelacion(businessId: number): Observable<CancellationPolicyResponse> {
    return this.http.get<CancellationPolicyResponse>(`${this.API_URL}/${businessId}/cancellation-policy`).pipe(
      catchError(this.handleError)
    );
  }

  guardarPoliticaCancelacion(businessId: number, policy: CancellationPolicyInput): Observable<CancellationPolicyResponse> {
    return this.http.put<CancellationPolicyResponse>(`${this.API_URL}/${businessId}/cancellation-policy`, policy).pipe(
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = friendlyError(error, 'Ocurrió un error. Intenta nuevamente.');
    console.error('Error HTTP:', error);
    return throwError(() => errorMessage);
  }
}



